import React, { useContext, useState } from 'react';

import classnames from 'classnames';
import { Maximize2, SidebarOpen } from 'lucide-react';

import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import Helmet from 'common/helmets/Helmet';
import Button from 'common/inputs/Button';
import Link from 'common/Link';
import AdminUsersDetailsSidebar from 'common/subdomain/admin/AdminUsersDetailsSidebar';

import AdminInboxItemList from './AdminInboxItemList';
import AdminInboxSidebar from './AdminInboxSidebar';

import type { QueryParams } from 'common/actions/inboxItemQueries';
import type { Board } from 'common/api/endpoints/boards';
import type { CustomField } from 'common/api/endpoints/customFields';
import type { CompanyUser } from 'common/api/endpoints/users';
import type { InboxItem } from 'common/api/resources/inbox';
import type { ReduxStateMetadata } from 'common/subdomain/admin/AdminUsers';

import 'css/components/subdomain/admin/AdminInbox/_AdminInbox.scss';

type Props = {
  boards: Board[];
  customFields: CustomField[];
  inboxItemList: {
    items: InboxItem[];
    loading: boolean;
    loadingMore: boolean;
    lastCreated: Date;
    queryParams: QueryParams;
  };
  inboxTotalItemCount: number;
  user?: CompanyUser & ReduxStateMetadata;
};

const AdminInbox = ({ boards, customFields, inboxItemList, inboxTotalItemCount, user }: Props) => {
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  const [sidebarClosed, setSidebarClosed] = useState<boolean>(false);

  const onCloseSidebar = () => {
    setSidebarClosed(true);

    const queryClone = { ...location.query };
    delete queryClone.userURLName;

    router.replace({
      ...location,
      query: queryClone,
    });
  };

  const renderUserSidebar = () => {
    const { userURLName } = location.query;
    const userIsLoaded = user && !user.loading && !user.error && !user.notFound;

    if (!userURLName || !userIsLoaded) {
      return (
        <div className={classnames('userSidebarContainer', { containerHidden: sidebarClosed })} />
      );
    }

    return (
      <div className="userSidebarContainer containerVisible">
        <div className="topActionContainer">
          <Button
            buttonType="ghostButton"
            className="iconButton"
            onTap={onCloseSidebar}
            value={<SidebarOpen size={18} />}
          />
          <Link to={`/admin/users/${user.urlName}?search=${encodeURIComponent(user.name)}`}>
            <Button
              buttonType="ghostButton"
              className="iconButton"
              value={<Maximize2 size={18} />}
            />
          </Link>
        </div>
        <AdminUsersDetailsSidebar customFields={customFields} user={user} />
      </div>
    );
  };

  return (
    <div className="adminInbox">
      <Helmet title="Inbox | Canny" />
      <AdminInboxSidebar boards={boards} inboxTotalItemCount={inboxTotalItemCount} />
      <AdminInboxItemList itemList={inboxItemList} />
      {renderUserSidebar()}
    </div>
  );
};

export default AdminInbox;
